import { useEffect, useRef } from 'react';
import { A } from '../router';
import { SITE, usePageMeta, absUrl, ogImageUrl } from '../siteMeta';
import { PageShell } from './Pages';

/**
 * Devlog: longer-form, behind-the-scenes development notes (distinct from the
 * player-facing /updates changelog and the in-universe /wiki). Same prerender +
 * SEO/GEO plumbing as the other pages: each post is in ROUTES, gets a Markdown
 * mirror, an OG card, a sitemap entry and BlogPosting structured data.
 */

export interface DevlogPost {
  slug: string;
  /** YYYY-MM-DD. */
  date: string;
  title: string;
  /** Meta description and index excerpt. */
  description: string;
  body: React.ReactNode;
}

/** The committed snapshot of the simulator's report (a public static asset). */
const REPORT_SRC = '/devlog/balance-simulator-report.html';

/** Same-origin iframe that grows to fit the static report it loads (no nested
 *  scrollbar). Falls back to a sensible min-height before/without JS. */
function ReportEmbed() {
  const ref = useRef<HTMLIFrameElement>(null);
  const fit = (): void => {
    const el = ref.current;
    try {
      const doc = el?.contentDocument;
      if (doc?.body) el!.style.height = `${doc.documentElement.scrollHeight}px`;
    } catch {
      /* same-origin only; ignore if blocked */
    }
  };
  useEffect(() => {
    const t = setTimeout(fit, 400); // give inline SVG a beat to lay out
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="devlog-report">
      <p className="devlog-report-link">
        <a href={REPORT_SRC} target="_blank" rel="noreferrer">Open the full report in a new tab ↗</a>
      </p>
      <iframe
        ref={ref}
        src={REPORT_SRC}
        title="Baseline economy simulation report"
        className="devlog-report-frame"
        loading="lazy"
        onLoad={fit}
      />
    </div>
  );
}

const POSTS: DevlogPost[] = [
  {
    slug: 'balance-simulator',
    date: '2026-06-16',
    title: 'I built a simulator to balance the game without playing it for months',
    description:
      'I heard the feedback and built a simulator that plays the whole game thousands of ways. It found a wall, a cliff, and what MechMaxxing needs.',
    body: (
      <>
        <p>
          First: I have heard your feedback, and thank you for it. The notes about progression
          stalling, and about builds that feel either unstoppable or stuck, all landed. Rather than
          guess at fixes, I wanted to see the whole game the way you actually play it.
        </p>
        <p>
          So I built a simulator. It is not a spreadsheet guess. It drives the real game engine, the
          same code that runs when you play, and plays entire runs from the first wave to the final
          overlord. It does this thousands of times, as different kinds of player, in minutes instead
          of months. I pointed it squarely at <A to="/wiki/mechmaxxing">MechMaxxing</A>, because that
          is the system doing the most to shape how a run feels.
        </p>
        <h2>Two findings that matter</h2>
        <p>
          <strong>If you play fair and do not chain your boosters, you hit a wall.</strong> Wire your
          amplifiers in parallel, one power tap each, the safe and tidy way, and your damage adds up
          but never compounds. Around the seventh overlord it simply stops keeping pace with the
          enemy, and no amount of grinding gets you past it.
        </p>
        <p>
          <strong>If you chain your boosters aggressively, you destroy the game in no time.</strong>{' '}
          Run those same amplifiers in a series, each one feeding the next into a single weapon, and
          the multipliers compound. The wall vanishes. The simulated min-maxer cleared all eleven
          overlords in about a week of calendar time and a couple dozen hours of actual play, far
          faster than this game is meant to last.
        </p>
        <h2>What that tells me</h2>
        <p>
          MechMaxxing and booster chaining are not a side trick. The chaining is the thing that lets
          you progress smoothly instead of slamming into a wall, so it needs to be there. But right
          now it is all or nothing: chain and trivialize the game, or do not and stall out. That is a
          cliff, not a curve.
        </p>
        <p>
          The work ahead is tuning chaining into a sweet spot, where building a strong series is
          rewarding and skillful without erasing the rest of the game. I am going to sit with this
          over the coming days and come back with a solid plan rather than a knee-jerk nerf.
        </p>
        <p>
          In parallel I am taking a hard look at the currencies, and at how different play styles can
          stay inside the timeline this game is built for. The goal is a real balance between being on
          the grind and being able to make meaningful progress when you come back for more, an
          experience that rewards you whether you push hard or check in casually.
        </p>
        <h2>See it for yourself</h2>
        <p>
          Here is the report the simulator produced, so you can see the difference between nuking the
          game and hitting the wall with your own eyes. It is a snapshot of the current build, before
          any changes. Three playstyles, measured against the intended timeline: the aggressive
          chained build runs the table, while the fair parallel builds stop at overlord seven.
        </p>
        <ReportEmbed />
        <p>
          Nothing in the live game has changed yet. This is the measurement that comes before the
          tuning, and I wanted to share it openly. Keep the feedback coming in the{' '}
          <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a>; it is shaping
          exactly this.
        </p>
      </>
    ),
  },
];

export const DEVLOG_ROUTES: readonly string[] = ['/devlog', ...POSTS.map((p) => `/devlog/${p.slug}`)];

function postLd(post: DevlogPost): object {
  const ld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    inLanguage: 'en',
    image: ogImageUrl(`/devlog/${post.slug}`),
    author: { '@type': 'Person', name: SITE.devHandle },
    publisher: { '@type': 'Person', name: SITE.devHandle },
    datePublished: post.date,
    dateModified: post.date,
  };
  const url = absUrl(`/devlog/${post.slug}`);
  if (url) {
    ld.url = url;
    ld.mainEntityOfPage = url;
  }
  return ld;
}

export function DevlogIndexPage() {
  usePageMeta({
    path: '/devlog',
    title: `Devlog | ${SITE.name}`,
    description:
      'Behind-the-scenes development notes for Big Mech Energy Idle: how the game is built, measured and balanced.',
    markdownPath: '/devlog.md',
  });
  return (
    <PageShell suffix="Devlog">
      <h1 className="wiki-h1">Devlog</h1>
      <p>
        Longer notes from behind the curtain: how the game is built, measured and tuned. Player-facing
        patch notes live on the <A to="/updates">Updates</A> page.
      </p>
      {POSTS.map((p) => (
        <section key={p.slug} className="devlog-list-item">
          <h2 className="update-title"><A to={`/devlog/${p.slug}`}>{p.title}</A></h2>
          <div className="update-date">{p.date}</div>
          <p>{p.description}</p>
          <p><A to={`/devlog/${p.slug}`}>Read more →</A></p>
        </section>
      ))}
    </PageShell>
  );
}

export function DevlogPostPage({ slug }: { slug: string }) {
  const post = POSTS.find((p) => p.slug === slug);
  usePageMeta(
    post
      ? {
          path: `/devlog/${post.slug}`,
          title: `${post.title} | ${SITE.name}`,
          description: post.description,
          markdownPath: `/devlog/${post.slug}.md`,
          breadcrumb: [
            { name: 'Devlog', path: '/devlog' },
            { name: post.title, path: `/devlog/${post.slug}` },
          ],
          jsonLd: [postLd(post)],
        }
      : { path: '/devlog', title: `Devlog | ${SITE.name}`, description: 'Development notes.', noindex: true },
  );
  if (!post) {
    return (
      <PageShell suffix="Devlog">
        <h1 className="wiki-h1">Post not found</h1>
        <p>That devlog entry does not exist. <A to="/devlog">Back to the devlog</A>.</p>
      </PageShell>
    );
  }
  return (
    <PageShell suffix="Devlog">
      <p className="crumbs"><A to="/devlog">Devlog</A></p>
      <h1 className="wiki-h1">{post.title}</h1>
      <div className="update-date">{post.date}</div>
      {post.body}
    </PageShell>
  );
}
